// The tree's own hits of a running free-text search - system, family and group nodes whose name
// matches - listed BELOW the article hits on the results page (customer decision: articles first,
// then the tree, anything else gets too complicated). Each level is its own section, in tree
// order from the bottom up (System, Familie, Gruppe); empty levels are left out, long ones capped
// like a facet value list. Clicking a node hands it to the page, which clears the search and
// selects the node - "go there", the same as a click in the category tree. Data: engine.searchTree.
//
// `compact` is the landing page's variant inside SearchMask's instant-answer dropdown: a few hits
// per level, no expand button, tighter type - a shortcut list, not a results page.

import { Anchor, Group, Stack, Text, UnstyledButton } from '@mantine/core';
import { useState } from 'react';

import { useCatalogLabels } from '../labels';
import { TEXT_ACTION_CLASS, VALUE_LIST_LIMIT } from './FacetGroup';

import type { TreeSearchHit, TreeSearchHits as TreeSearchHitsData } from '../engine';

interface TreeSearchHitsProps {
    hits: TreeSearchHitsData;
    onSelect: (nodeId: string) => void;
    compact?: boolean;
}

/** Hits per level in the compact (landing page dropdown) variant. */
const COMPACT_LIMIT = 3;

const Level = ({
    title,
    hits,
    compact,
    onSelect,
}: {
    title: string;
    hits: TreeSearchHit[];
    compact: boolean;
    onSelect: (nodeId: string) => void;
}) => {
    const { L } = useCatalogLabels();
    const [expanded, setExpanded] = useState(false);
    if (hits.length === 0) {
        return null;
    }
    const limit = compact ? COMPACT_LIMIT : VALUE_LIST_LIMIT;
    const visible = expanded ? hits : hits.slice(0, limit);
    const hidden = hits.length - visible.length;
    return (
        <div>
            <Text c={compact ? 'dimmed' : undefined} fw={compact ? 500 : 700} mb={4} size={compact ? 'xs' : 'sm'}>
                {title} ({hits.length})
            </Text>
            <Stack gap={4}>
                {visible.map((hit) => (
                    // Compact: one line per hit like the article rows above it in the dropdown -
                    // label left, path right and truncated. Full: the path may wrap below.
                    <Group
                        key={hit.nodeId}
                        align="baseline"
                        gap={compact ? 'sm' : 'xs'}
                        justify={compact ? 'space-between' : 'flex-start'}
                        wrap={compact ? 'nowrap' : 'wrap'}
                    >
                        {/* A real button: focusable and Enter/Space-activatable, unlike an
                            href-less <a>. */}
                        <Anchor
                            component="button"
                            size="sm"
                            style={{ minWidth: 0, textAlign: 'left' }}
                            type="button"
                            onClick={() => onSelect(hit.nodeId)}
                        >
                            {hit.label}
                        </Anchor>
                        {hit.path && (
                            <Text
                                c="dimmed"
                                size="xs"
                                style={compact ? { flex: 1, minWidth: 0, textAlign: 'right' } : undefined}
                                truncate={compact}
                            >
                                {hit.path}
                            </Text>
                        )}
                    </Group>
                ))}
                {!compact && hits.length > VALUE_LIST_LIMIT && (
                    <UnstyledButton
                        className={`${TEXT_ACTION_CLASS} self-start`}
                        onClick={() => setExpanded((prev) => !prev)}
                    >
                        {expanded ? L.facetShowLess : L.facetShowMore.replace('{count}', String(hidden))}
                    </UnstyledButton>
                )}
            </Stack>
        </div>
    );
};

export const TreeSearchHits = ({ hits, onSelect, compact = false }: TreeSearchHitsProps) => {
    const { L } = useCatalogLabels();
    if (hits.system.length + hits.family.length + hits.group.length === 0) {
        return null;
    }
    return (
        <Stack gap={compact ? 'xs' : 'md'} mt={compact ? 0 : 'lg'}>
            <Text fw={700} size={compact ? 'sm' : undefined}>
                {L.treeHits}
            </Text>
            <Level compact={compact} hits={hits.system} title={L.searchMaskSystem} onSelect={onSelect} />
            <Level compact={compact} hits={hits.family} title={L.searchMaskFamily} onSelect={onSelect} />
            <Level compact={compact} hits={hits.group} title={L.searchMaskGroup} onSelect={onSelect} />
        </Stack>
    );
};
